import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

// Components
import OrderTable from '../OrderTable';
import OrderTotals from '../OrderTotals';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import OrderCard from '../OrderCard';
import Skeleton from '@mui/material/Skeleton';
import AlertMessage from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import Alert from '../Alert';
import DownloadOrder from './DownloadOrder';

// Queries
import getAgregarAlCarrito from '../Queries/getAgregarAlCarrito';
import GetTokenIrelandPay from '../Queries/GetTokenIrelandPay';
import getGlobal from '../Queries/getGlobal';

// Styles
import styles from './index.module.sass';

// Images
import MasterCard from '../../images/methods/master-cart.svg';
import Visa from '../../images/methods/visa.svg';
import Amex from '../../images/methods/amex.svg';

export default function OrderDetail({
  history,
  match,
  getCookie,
  webservice,
  device,
  setSessionMessage,
}) {
  const [initialState, setInitialState] = useState(false);
  const [alertOpen, setalertOpen] = useState(false);
  const [alertText, setAlertText] = useState('');
  const [orderData, setOrderData] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingAddress, setLoaddingAddress] = useState(false);
  const [finishLoading, setFinishLoading] = useState(false);
  const { orderMessage = '' } = useParams();
  const [openMessage, setOpenMessage] = useState(orderMessage === 'error');
  const [extraShippingharge, setExtraShippingharge] = useState(
    orderData?.domicilioEnvio?.confirmacionEnvio ||
      orderData?.domicilioEnvio?.bloquearCambioDomicilio === 'true' ||
      false,
  );
  const [paqueterias, setPaqueterias] = useState([]);
  const [paqueteria, setPaqueteria] = useState('');

  const { cliente = {}, domicilioEnvio = {}, totales = {}, folioPedido } = orderData;
  const { importeAPagar = '0' } = totales;
  const { nombreCompleto = '', calle = '', colonia = '', municipio = '', cp } = cliente;

  const { t } = useTranslation(['order', 'address']);

  const { screenSize } = device;

  const getOrder = useCallback(
    async (action, cat, id, quantity, lineNum, neddLoading) => {
      setLoading(neddLoading);
      setFinishLoading(true);
      const order = await getAgregarAlCarrito({
        jsonBody: {
          accion: action,
          usuarioOK: getCookie('usuarioOk'),
          folioPedido: getCookie('folioPedido'),
          almacen: getCookie('warehouse'),
          categoria: cat,
          clave: id,
          cantidad: quantity,
          numeroLinea: lineNum,
          textoProdSeleccion: '',
          idioma: window?.localStorage?.getItem('i18nextLng')?.includes('es') ? 0 : 1,
          origen: 'CARRITONUE',
        },
      });
      const { tipoMensaje, mensaje } = order;
      if (mensaje !== '' && tipoMensaje === 'error') {
        setAlertText(mensaje);
        setalertOpen(true);
      }

      setOrderData(order || {});

      setExtraShippingharge(
        order?.domicilioEnvio?.confirmacionEnvio ||
          orderData?.domicilioEnvio?.bloquearCambioDomicilio === 'true' ||
          false,
      );

      const getPaqueterias = await getGlobal({
        tmbservice: 'getPaqueterias',
        jsonBody: {
          estado: order?.domicilioEnvio?.colonia || '',
        },
      });

      const {
        typeMessage: typeMessagePaq = '',
        message: messagePaq = '',
        dataDetallePaqueteria = [],
      } = getPaqueterias;

      if (messagePaq !== '' && typeMessagePaq === 'error') {
        setAlertText(messagePaq);
        setalertOpen(true);
      }

      setPaqueterias(dataDetallePaqueteria);
      setPaqueteria(order?.domicilioEnvio?.paqueteria || '');

      setLoading(false);
      setFinishLoading(false);

      setSessionMessage(order?.mensaje || '');
    },
    [getCookie, orderData?.domicilioEnvio?.bloquearCambioDomicilio, setSessionMessage],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!initialState) {
      setInitialState(true);
      getOrder('CERRAR01WEB', '', '', '', '', true);
    }
  }, [getOrder, t, initialState]);

  async function handleClick() {
    if (!paqueteria) {
      setAlertText(t('order:parcelAlert'));
      setalertOpen(true);
      return;
    }

    setFinishLoading(true);

    const irelandPayToken = await GetTokenIrelandPay({
      jsonBody: { idioma: 1, amount: importeAPagar, orderNumber: folioPedido },
    });

    const { token = '', message = '', typeMessage = 'error' } = irelandPayToken;

    setSessionMessage(message);

    if (token && typeMessage === 'success') {
      history.push(`${match.path}/payment/${token}`);
    } else {
      setFinishLoading(false);
      setOpenMessage(true);
      window.scrollTo(0, 0);
    }
  }

  return (
    <div className={`${styles.container} ${styles[screenSize]}`}>
      <Alert open={alertOpen} setOpen={setalertOpen} title={'Terramar'} text={alertText} />
      {(orderMessage === 'error' || openMessage) && (
        <div className={styles.alertMessage}>
          <Collapse in={openMessage}>
            <AlertMessage
              severity='error'
              action={
                <div className={styles.closeContainer}>
                  <span
                    className={`material-icons-outlined ${styles.closeMessage}`}
                    onClick={() => {
                      setOpenMessage(false);
                    }}>
                    close
                  </span>
                </div>
              }
              sx={{ mb: 2 }}>
              The transaction could not be concluded successfully, please try again
            </AlertMessage>
          </Collapse>
        </div>
      )}
      <div className={`${styles.orderDetail}`}>
        <div className={styles.content}>
          <div className={styles.leftRow}>
            <OrderCard>
              {screenSize !== 'phone' && (
                <div className={styles.kitData}>
                  <div className={`${styles.kitName}`}>
                    <div className={styles.cardText}>{t('name')}:</div>
                    <div className={styles.num}>{getCookie('kitName')}</div>
                  </div>
                  <div className={`${styles.kitNumber}`}>
                    <div className={styles.cardText}>{t('kit')}:</div>
                    <div className={styles.num}>{getCookie('usuario')}</div>
                  </div>
                </div>
              )}

              <div className={styles.orderNum}>
                <div className={styles.cardText}>{t('orderNumber')}:</div>
                <div className={styles.num}>{getCookie('folioPedido')}</div>
              </div>
            </OrderCard>

            <div className={styles.info}>
              <div className={styles.orderInfo}>
                <div className={styles.infoMessage}>
                  <div className={`${styles.blue}`}>{orderData?.mensaje || ''}</div>
                  {orderData?.situacionCierreMensaje && (
                    <div className={`${styles.alert}`}>
                      {t('closingStatus')}: {orderData.situacionCierreMensaje}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <OrderCard title={t('cartTitle')}>
              <div className={styles.orderTable}>
                <OrderTable order={orderData} getOrder={getOrder} device={device} />
              </div>
            </OrderCard>

            {screenSize === 'phone' && (
              <div className={styles.orderTotals}>
                <OrderTotals
                  totals={orderData?.totales || []}
                  device={device || {}}
                  loading={loading}
                  state={domicilioEnvio?.colonia ?? ''}
                />
              </div>
            )}

            <div className={styles.sectionTitle}>
              <div className={styles.titleContainer}>
                <i className={`material-icons ${styles.sectionicon}`}>location_on</i>
                <div className={styles.sectiontext}>{t('billingAddress')}:</div>
              </div>
            </div>
            <OrderCard>
              <div className={styles.billingAddress}>
                {(loading && (
                  <Skeleton variant='rectangular' animation='wave' className={styles.loadingText} />
                )) || <div className={styles.billingName}>{nombreCompleto}</div>}
                {!loading && (
                  <div className={styles.billingText}>
                    {calle}, {municipio}, {colonia}, {cp}
                  </div>
                )}
              </div>
            </OrderCard>

            <div className={styles.sectionTitle}>
              <div className={styles.titleContainer}>
                <i className={`material-icons-outlined ${styles.sectionicon}`}>local_shipping</i>
                <div className={styles.sectiontext}>{t('shippingAddress')}:</div>
              </div>
            </div>
            <OrderCard>
              <>
                <div className={styles.shippingAddress}>
                  <div className={styles.orderDelivery}>
                    <div className={styles.cardText}>
                      {((loading || loadingAddress) && (
                        <Skeleton
                          variant='rectangular'
                          animation='wave'
                          className={styles.loadingText}
                        />
                      )) ||
                        (domicilioEnvio.domicilioClienteFinalSN === 'S' &&
                          domicilioEnvio.domicilioClienteFinalNombre) ||
                        cliente?.nombreCompleto ||
                        ''}
                    </div>
                    <div className={styles.num}>
                      {((loading || loadingAddress) && (
                        <Skeleton
                          variant='rectangular'
                          animation='wave'
                          className={styles.loadingText}
                        />
                      )) || (
                        <>
                          <div>
                            {domicilioEnvio?.calle || ''} {domicilioEnvio?.entreCalle || ''}{' '}
                            {domicilioEnvio?.numeroExterior || ''}
                          </div>
                          <div>
                            {domicilioEnvio?.municipio || ''}
                            {', '}
                            {domicilioEnvio?.colonia || ''}
                          </div>
                          <div>PC {domicilioEnvio?.cp || ''}</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                {(orderData?.domicilioEnvio?.paqueteria === 'FEDEX' ||
                  orderData?.domicilioEnvio?.paqueteria === 'UPS') && (
                  <div className={styles.checkContainer}>
                    {orderData?.domicilioEnvio?.confirmacionEnvio && (
                      <span className={styles.colorGold}>
                        ✅ {t('address:extraShippingCharge')}
                      </span>
                    )}
                  </div>
                )}
              </>
            </OrderCard>
          </div>
          <div className={styles.rightRow}>
            {screenSize !== 'phone' && (
              <div className={styles.orderTotals}>
                <OrderTotals
                  totals={orderData?.totales || []}
                  device={device || {}}
                  loading={loading}
                />

                <OrderCard>
                  <div className={styles.orderDet}>
                    <div className={styles.cardTextPaq}>
                      <div className={styles.textIcon}>
                        <i className={`material-icons ${styles.icon}`}>local_shipping </i>
                        <label className={styles.iconText}>{t('parcelSelected')}</label>
                      </div>
                    </div>
                    <div className={styles.parcelSelected}>{paqueteria}</div>
                  </div>
                </OrderCard>

                {screenSize !== 'phone' && (
                  <div className={styles.accionButton}>
                    <Button
                      type='submit'
                      variant='contained'
                      size='large'
                      color='primary'
                      className={
                        (orderData.situacionCierre !== 'LISTO' && styles.disabled) || styles.button
                      }
                      onClick={() => {
                        // history.push(`${match.path}/checkout`)
                        if (!finishLoading) handleClick();
                      }}
                      disabled={orderData.situacionCierre !== 'LISTO'}>
                      {t('finishOrder')}
                      {(finishLoading && <CircularProgress className={styles.finichLoading} />) || (
                        <i className={`material-icons ${styles.buttonIcon}`}>trending_flat</i>
                      )}
                    </Button>
                    <div className={styles.termsAlert}>{t('termsAlert')}</div>
                    <div className={styles.cardsIcons}>
                      <img className={styles.cardIcon} src={MasterCard} alt='master card icon' />
                      <img className={styles.cardIcon} src={Visa} alt='visa icon' />
                      <img className={styles.cardIcon} src={Amex} alt='amex icon' />
                    </div>
                    <DownloadOrder orderData={orderData} />
                  </div>
                )}
              </div>
            )}
          </div>
          {/*&& orderData?.domicilioEnvio?.colonia === 'PR' */}
          {screenSize === 'phone' && (
            <OrderCard>
              <div className={styles.orderDet}>
                <div className={styles.cardTextPaq}>
                  <div className={styles.textIcon}>
                    <i className={`material-icons ${styles.icon}`}>local_shipping </i>
                    <label className={styles.iconText}>{t('parcelSelected')}</label>
                  </div>
                </div>
                <div className={styles.parcelSelected}>{paqueteria}</div>
              </div>
            </OrderCard>
          )}
        </div>
      </div>
      {screenSize === 'phone' && (
        <div className={styles.accionButton}>
          <Button
            type='submit'
            variant='contained'
            size='large'
            color='primary'
            className={(orderData.situacionCierre !== 'LISTO' && styles.disabled) || styles.button}
            onClick={() => {
              if (!finishLoading) handleClick();
            }}
            disabled={orderData.situacionCierre !== 'LISTO'}>
            {t('finishOrder')}
            {(finishLoading && <CircularProgress className={styles.finichLoading} />) || (
              <i className={`material-icons ${styles.buttonIcon}`}>trending_flat</i>
            )}
          </Button>
          <div className={styles.termsAlert}>{t('termsAlert')}</div>
          <div className={styles.cardsIcons}>
            <img className={styles.cardIcon} src={MasterCard} alt='master card icon' />
            <img className={styles.cardIcon} src={Visa} alt='visa icon' />
            <img className={styles.cardIcon} src={Amex} alt='amex icon' />
          </div>
          <DownloadOrder orderData={orderData} />
        </div>
      )}
    </div>
  );
}
